import { useParams } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { TICKET_PRIORITIES } from "../constants";
import { useListAttachments } from "../hooks/useListAttachments";
import { useShowConversation } from "../hooks/useShowConversation";
import type { HelpWidgetConversation } from "../types/help_widget_conversation";
import { AttachmentRow } from "./common/AttachmentRow";
import { FormResponseItem } from "./common/FormResponseItem";
import { HelpWidgetLayout } from "./common/HelpWidgetLayout";
import { SkeletonLoader } from "./common/SkeletonLoader";

export const BugReportConfirmationView = () => {
  const { id } = useParams<{ id: string }>();
  const {
    data: conversation,
    isLoading,
    isError,
  } = useShowConversation(id) as {
    data: HelpWidgetConversation | undefined;
    isLoading: boolean;
    isError: boolean;
  };
  const { data } = useListAttachments({ conversationId: id, all: true });
  const attachments = data?.attachments || [];

  if (isError) {
    return <div>{t("help_widget.send_message_form.something_went_wrong")}</div>;
  }

  const priorityText =
    TICKET_PRIORITIES.find(p => p.value === conversation?.data?.priority)
      ?.text || TICKET_PRIORITIES[0].text;

  if (isLoading) {
    return (
      <div className="flex size-full flex-1 flex-col p-4">
        <SkeletonLoader rows={4} height="52px" wrapperClassName="!px-0" />
      </div>
    );
  }

  return (
    <HelpWidgetLayout className="relative flex-1 overflow-hidden">
      <div className="scrollbar-hide absolute inset-0 overflow-y-auto p-4">
        <div className="my-6 flex flex-col items-center">
          <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-green-500">
            <Icon type="36-check" size={36} className="!w-9 text-white" />
          </div>
          <div className="mt-2 text-center">
            <Typography.TitleMd weight="bold">
              {t("help_widget.send_message_form.message_sent_successfully")}
            </Typography.TitleMd>
          </div>
          <div className="mt-2 text-center">
            <Typography.BodySm color="text-tertiary">
              {t("help_widget.send_message_form.notify_message")}
            </Typography.BodySm>
          </div>
        </div>

        <FormResponseItem
          question={t("help_widget.send_message_form.type")}
          answer={conversation?.data?.type || ""}
        />
        <FormResponseItem
          question={t("help_widget.send_message_form.priority")}
          answer={priorityText}
        />
        <FormResponseItem
          question={t("help_widget.send_message_form.text")}
          answer={conversation?.data?.text || ""}
        />
        {attachments.length > 0 && (
          <div className="mt-6 flex flex-col gap-2">
            {attachments.map(attachment => (
              <AttachmentRow
                key={attachment.id}
                url={attachment.url}
                filename={attachment.filename}
              />
            ))}
          </div>
        )}
      </div>
    </HelpWidgetLayout>
  );
};
